import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import type { ElementSelection } from '@crowdex/plugin-contracts';
import { Button } from '@crowdex/ui/components/button';
import { Input } from '@crowdex/ui/components/input';
import { ArrowUp, MousePointer2, X } from 'lucide-react';
import { useState } from 'react';
import { desktopPreviewState } from '@/lib/desktop-preview-state';
import { askAboutElements, useArtifactAttach } from './agent-ask';

/** "Point at an element" (D15): the artifact's composer takes what is said about it. */
export function focusElementComposer() {
  document.querySelector<HTMLInputElement>('[data-artifact-composer] input')?.focus();
}

/**
 * The composer under an artifact open in the panel (design D15, mobile 12): what is typed there
 * is about the elements pointed at in the artifact's view. Pointing is the app's (its comments
 * call the core's `attach`, the one system of element comments): while this composer is open,
 * what the artifact's view attaches comes here, as its chips, not to the conversation's composer.
 * The message goes to the artifact's own conversation, with the elements, as the user would send
 * it there.
 */
export function ArtifactElementComposer({ artifact }: { artifact: ArtifactListingEntry }) {
  const [elements, setElements] = useState<readonly ElementSelection[]>(
    () => desktopPreviewState.getState().artifactPointed[artifact.id] ?? [],
  );
  useArtifactAttach(artifact.id, (selections) =>
    setElements((current) => [...current, ...selections]),
  );
  const [text, setText] = useState('');
  const pointed = elements.length > 0;
  const send = () => {
    const message = text.trim();
    if (message === '') return;
    askAboutElements(
      pointed ? message : `${message}\n\n(About ${artifact.title}.)`,
      [...elements],
      artifact.conversation ?? undefined,
    );
    setElements([]);
    setText('');
  };
  return (
    <form
      aria-label={`Change ${artifact.title}`}
      className="m-3 flex items-center gap-2 rounded-composer border border-border bg-card p-1.5 pl-2"
      data-artifact-composer
      onSubmit={(event) => {
        event.preventDefault();
        send();
      }}
    >
      {elements.map((element, index) => (
        <span
          className="type-meta flex max-w-56 shrink-0 items-center gap-1.5 rounded-control bg-control px-2.5 py-1 text-foreground"
          // biome-ignore lint/suspicious/noArrayIndexKey: The same element can be picked twice; picks are positions in the list.
          key={`${element.selector}-${index}`}
        >
          <MousePointer2 aria-hidden className="size-3.5 shrink-0" />
          <span className="truncate">{element.title}</span>
          <button
            aria-label={`Forget ${element.title}`}
            className="text-muted-foreground hover:text-foreground"
            onClick={() => setElements((current) => current.filter((_, at) => at !== index))}
            type="button"
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
      <Input
        aria-label="What to change"
        appearance="bare"
        className="h-8 min-w-0 flex-1 px-1"
        onChange={(event) => setText(event.target.value)}
        placeholder={
          pointed
            ? 'Change this element…'
            : 'Point at an element in the view, or describe a change…'
        }
        value={text}
      />
      <Button
        aria-label="Send"
        className="size-8 shrink-0"
        disabled={text.trim() === ''}
        size="icon-sm"
        type="submit"
      >
        <ArrowUp className="size-4" />
      </Button>
    </form>
  );
}
